import { FC, useEffect, useRef } from 'react';

import { formatCostInCents } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { Settings } from 'utils/constants/dynamicSettings';

import multicreationSetRecommendedBudgetButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_set_recommended_budget_button_click';
import { ActionBar, Button, Text, useBreakpoint } from '@hh.ru/magritte-ui';
import { ExclamationCircleFilledSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import { useMultiCreationBidBudgetValidation } from 'hooks/multiCreation/useMultiCreationBidBudgetValidation';
import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import { AuctionAutoStrategySettings } from 'components/AuctionBuilder/types/auctionAutoStrategySettings';
import { MoneyInput } from 'components/common/MoneyInput/MoneyInput';
import { TooltipCommon } from 'components/common/TooltipCommon/TooltipCommon';

import { BiddingStrategy } from 'store/models/auctionData';
import {
    selectMultiCreationCampaignsCreating,
    selectMultiCreationVacanciesAuctionInfo,
    setMultiCreationBudget,
} from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTableBody/components/BudgetInput/BudgetInput.module.less';

const TrlKeys = {
    budgetPlaceholder: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.budget.placeholder',
    tooltip: {
        content: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.budget.tooltip.content',
        close: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.budget.tooltip.close',
        apply: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.budget.tooltip.apply',
        currency: 'currency.short',
    },
    error: {
        valueRequired: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.budget.error.valueRequired',
        minValue: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.budget.error.minValue',
    },
};

export const BudgetInput: FC<{ vacancyId: number }> = ({ vacancyId }) => {
    const currentVacanciesAuctionInfo = useSelector(selectMultiCreationVacanciesAuctionInfo)[vacancyId];
    const currentAuctionsToCreate = useSelector(({ multiCreation }) => multiCreation.auctionsToCreate[vacancyId]);
    const biddingStrategy = useSelector(({ multiCreation }) => multiCreation.biddingStrategy);
    const isCampaignsCreating = useSelector(selectMultiCreationCampaignsCreating);
    const [isTooltipVisible, setIsTooltipVisibleOn, setIsTooltipVisibleOff] = useOnOffState(false);

    const { budget, budgetValidationError } = currentAuctionsToCreate;
    const minBudget = currentVacanciesAuctionInfo.budget.minValue;

    const { isMobile } = useBreakpoint();
    const trls = useTrls();
    const settings = useSettings();
    const dispatch = useDispatch();
    const { validateBudget } = useMultiCreationBidBudgetValidation({ vacancyId });
    const activatorRef = useRef(null);
    const { recommendedMinBudgetInCents: recommendedMinBudgetRaw } =
        (settings(Settings.AuctionAutoStrategySettings) as AuctionAutoStrategySettings | null) || {};
    const recommendedMinBudgetInCents = Number(recommendedMinBudgetRaw);

    useEffect(() => {
        validateBudget(budget, minBudget);
    }, [budget, minBudget, validateBudget]);

    const isAutoStrategyLowBudget =
        Number.isFinite(recommendedMinBudgetInCents) &&
        Number(budget) < recommendedMinBudgetInCents &&
        biddingStrategy === BiddingStrategy.Auto;

    const iconProps = isAutoStrategyLowBudget
        ? {
              buttonIcon: ExclamationCircleFilledSize24,
              onButtonClick: setIsTooltipVisibleOn,
          }
        : {};

    const tooltipActions = isAutoStrategyLowBudget && (
        <>
            {isMobile && (
                <Button mode="secondary" style="accent" onClick={setIsTooltipVisibleOff}>
                    {trls(TrlKeys.tooltip.close)}
                </Button>
            )}
            <Button
                mode="primary"
                style={isMobile ? 'accent' : 'constant'}
                onClick={() => {
                    multicreationSetRecommendedBudgetButtonClick({
                        vacancyId,
                        recommendedBudget: recommendedMinBudgetInCents / 100,
                    });
                    dispatch(setMultiCreationBudget({ vacancyId, value: String(recommendedMinBudgetInCents) }));
                    setIsTooltipVisibleOff();
                }}
            >
                {trls(TrlKeys.tooltip.apply, [formatCostInCents(recommendedMinBudgetInCents, NON_BREAKING_SPACE)])}
                {NON_BREAKING_SPACE}
                {trls(TrlKeys.tooltip.currency)}
            </Button>
        </>
    );

    return (
        <>
            <div className={styles.wrapper}>
                <MoneyInput
                    {...iconProps}
                    data-qa="budget-input"
                    disabled={isCampaignsCreating}
                    elevatePlaceholder={isMobile}
                    buttonRef={activatorRef}
                    onChange={(value) => dispatch(setMultiCreationBudget({ vacancyId, value }))}
                    placeholder={trls(TrlKeys.budgetPlaceholder)}
                    size={isMobile ? 'large' : 'medium'}
                    value={budget}
                    invalid={!!budgetValidationError}
                    errorMessage={budgetValidationError || undefined}
                />
            </div>
            {isAutoStrategyLowBudget && (
                <TooltipCommon
                    isHoverTooltip
                    maxWidth={436}
                    activatorRef={activatorRef}
                    visible={isTooltipVisible}
                    tooltipContent={
                        <Text typography={isMobile ? 'label-1-regular' : 'paragraph-2-regular'}>
                            {trls(TrlKeys.tooltip.content)}
                        </Text>
                    }
                    onClose={setIsTooltipVisibleOff}
                    tooltipActions={tooltipActions}
                    bottomSheetFooter={<ActionBar primaryActions={tooltipActions} />}
                />
            )}
        </>
    );
};
